/**
 * threadPersist
 *
 * Defensive per-thread persistence helpers
 * (PR fix/thread-history-loss-and-multi-tab-sync, Miles 2026-05-10).
 *
 * Background
 * ──────────────────────────────────────────────────────────────────────
 * Miles reported on /justiceisurright (2026-05-10 21:43 UTC):
 *
 *   "one time I came back to a thread and all the message thread back
 *    and forth record was missing"
 *
 * Phase A diagnostic narrowed the failure modes to four interacting
 * paths in `useChatThreads` + `useGlobalChat`:
 *
 *  1. The per-thread persist effect (`useChatThreads` line 423) calls
 *     `setToLocalStorage(threadMsgKey(...), messages)` whenever
 *     `state.messages` changes for the active thread. With no
 *     defensive guard, an empty `state.messages` (e.g. from a stale
 *     SET_MESSAGES dispatched by the legacy CHAT_HISTORY mount-hydrate
 *     path on tab resume) overwrites the on-disk transcript with [].
 *  2. localStorage QuotaExceededError on long threads with attachments
 *     was unhandled in the per-thread path — `setToLocalStorage`
 *     silently swallows the error, so the on-disk transcript becomes
 *     stale or partial.
 *  3. Multi-tab same-thread races: two tabs writing the same per-thread
 *     key in rapid succession can clobber each other's content with
 *     last-write-wins.
 *  4. The legacy global `CHAT_HISTORY` mount-hydrate path (in
 *     `useGlobalChat`) re-dispatches stale legacy storage into
 *     `state.messages` on tab resume even with threading on; the
 *     per-thread persist effect then writes that stale snapshot to the
 *     active thread's per-thread key.
 *
 * Path #4 is fixed in `useGlobalChat` directly. Paths #1 / #2 / #3
 * are fixed by routing every per-thread write through
 * `safelyPersistThreadMessages` below.
 *
 * Behaviour
 * ──────────────────────────────────────────────────────────────────────
 * Given (instanceId, threadId, nextMessages, source):
 *
 *   • Read the existing on-disk content (localStorage; IDB fallback).
 *   • If `existing.length > 0 && next.length === 0` → SKIP the write
 *     and log `thread.history.persist-blocked`. This is the
 *     "you cannot wipe a non-empty thread with an empty snapshot"
 *     rule — the most direct guard against Miles's reported failure.
 *   • If `existing.length > 0 && next.length < existing.length / 2`
 *     → ALLOW the write (the user may have genuinely deleted
 *     messages) but log `thread.history.suspicious-shrink` so we
 *     can spot regressions in production.
 *   • Try `localStorage.setItem`. On QuotaExceededError, fall back
 *     to IndexedDB and log `thread.history.quota-exceeded` +
 *     `thread.history.indexeddb-fallback`.
 *   • On every successful write (including IDB fallback), broadcast
 *     a `BroadcastChannel('prest0-thread-sync')` message so other
 *     tabs of the same browser can rehydrate without waiting for
 *     localStorage `storage` events (which are debounced and
 *     unreliable on dormant tabs).
 *
 * Feature flag
 * ──────────────────────────────────────────────────────────────────────
 * All of the above can be disabled by setting
 * `window.__PRESTON_THREAD_HISTORY_GUARDS_DISABLED__ = true` before
 * the chat boots. With guards disabled the helper falls back to a
 * direct `setToLocalStorage` write — the legacy behaviour. This is
 * here to give ops a kill switch in case any guard fires too
 * aggressively in production.
 *
 * Telemetry sink
 * ──────────────────────────────────────────────────────────────────────
 * The codebase doesn't expose a centralized telemetry sink; the
 * existing storage path (`useGlobalChat`'s QuotaExceededError
 * handler) logs to `console.warn` with a prefixed tag. We follow
 * that pattern: `console.warn('[thread-iso:persist] <event>', { ...payload })`.
 * Future telemetry plumbing can `grep` for the prefixed tag and
 * forward.
 */

import { stripClientOnlyMessageFields } from '../../services/messageDelivery/clientOnlyMessageFields';
import {
  getGuestAdoptionMarkerKey,
  getGuestEraMarkerKey,
  getThreadStorageKeys,
  type ChatMessage,
} from './types';
import {
  getFromLocalStorage,
  setToLocalStorage,
} from './utils';
import { reclaimThreadCacheBytes, sweepStorageDuringSession } from './storageQuota';
import { flushDeferredThreadSave, holdRefusedWrite, readHeldCopy, releaseHeldCopiesWhere, releaseHeldCopy } from './threadCacheTier';
import { tombstoneStorageKey } from '../../services/groupThreads/archivedTombstones';
import { createIdbConnection } from '../../utils/idbConnection';
import { noteTiming } from '../../utils/chatHealthCounters';
import { newestLocalWindow, newestMessageTime } from './localCopyWindow';

/** localStorage key prefix for per-thread message snapshots. */
export const THREAD_MSG_KEY_PREFIX = 'prest0_thread_msgs__';

// ────────────────────────────────────────────────────────────────────────────
// Attachment sanitization for persistence (Miles 2026-05-11, thread
// `C0AM8U804KZ/1778281880.499349`).
//
// Background
// ──────────────────────────────────────────────────────────────────────
// `ChatMessage.attachments` is `File[]` — a runtime browser type whose
// instances do NOT survive `JSON.stringify`: every `File` becomes `{}`.
// Once a per-thread snapshot has been written and re-read, those File
// slots are present-but-empty `{}` objects with truthy length. The
// renderer's `AttachmentsSection.hasFiles` check (length > 0) then
// shadows the durable `attachmentsSerialized` fallback path, and the
// user bubble appears empty after a thread-switch / refresh /
// cross-device pickup.
//
// Fix shape: at every persist boundary, strip `attachments` from each
// message. `attachmentsSerialized` (which already carries `dataUrl` /
// `storageUrl` and IS round-trippable through JSON) becomes the sole
// durable record. Live in-memory `state.messages` keeps the File
// objects for fast re-rendering during the current session — they are
// only stripped at the persistence boundary.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when `value` looks like a real browser `File` (or `Blob`)
 * instance — has a `.name` and a numeric `.size`, optionally a
 * `.lastModified`. After `JSON.stringify` → `JSON.parse` round-trip a
 * File becomes `{}` (no own enumerable properties), so this returns
 * `false` for the dead post-rehydrate shells that caused the
 * thread-switch attachment-loss bug.
 *
 * Exported so renderer-side `hasFiles` checks can use the SAME
 * predicate the persistence layer uses to strip dead shells. Keeping
 * the predicate in one place is what makes the round-trip behaviour
 * deterministic.
 */
export function isRealFileLike(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  // Native `File` / `Blob` instance check when the runtime exposes them.
  // jsdom and the production browser both define `File`; the unit test
  // environment (`node-fetch`) does not, so the duck-typed branch below
  // is the fallback.
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  const v = value as { name?: unknown; size?: unknown };
  return typeof v.name === 'string'
    && v.name.length > 0
    && typeof v.size === 'number'
    && Number.isFinite(v.size);
}

/**
 * Strip non-serializable runtime values from a single message before
 * persistence. Today the only field that fails JSON-roundtrip is
 * `attachments: File[]`; we drop it entirely. The durable record is
 * `attachmentsSerialized` (`dataUrl` for in-session use, `storageUrl`
 * for cross-device pickup), which the renderer falls back to whenever
 * no real File-like values are present.
 *
 * Returns the original message reference when there is nothing to
 * strip — keeps the snapshot identity-stable for React diffing.
 */
export function sanitizeMessageForPersist<T extends ChatMessage>(message: T): T {
  if (!message || typeof message !== 'object') return message;
  // ONE list of client-only slots for BOTH persist boundaries (this one and
  // toMessageDoc): `attachments` (File[]) plus, since PR-2, the render-only
  // delivery annotations. `attachmentsSerialized` and `sharedMessageId` stay.
  return stripClientOnlyMessageFields(message);
}

/**
 * Strip the post-rehydrate `attachments: [{}, {}, ...]` shells that
 * `JSON.parse` produces from previously-persisted File arrays. After
 * any future write goes through `sanitizeMessagesForPersist` the
 * `attachments` slot is gone entirely; but threads written before
 * this fix landed may still have `attachments: [{}, {}]` shells in
 * localStorage. This helper removes them on rehydrate so the renderer
 * falls through to `attachmentsSerialized`.
 *
 * Returns the original message reference when there is nothing to
 * strip — keeps array identity stable for downstream React diffing.
 */
export function sanitizeMessageForRehydrate<T extends ChatMessage>(message: T): T {
  if (!message || typeof message !== 'object') return message;
  const m = message as ChatMessage & { attachments?: unknown };
  const att = m.attachments;
  if (!Array.isArray(att) || att.length === 0) return message;
  // If any element is a real File-like, keep the array as-is (would
  // only happen in tests that hand a live File through the rehydrate
  // path; production rehydrate runs through JSON.parse and never sees
  // real Files).
  if (att.some(isRealFileLike)) return message;
  // All elements are dead shells — drop the slot so the renderer falls
  // through to `attachmentsSerialized`.
  const { attachments: _drop, ...rest } = m as ChatMessage & { attachments?: unknown };
  void _drop;
  return rest as T;
}

/** Apply `sanitizeMessageForRehydrate` to an entire array. */
export function sanitizeMessagesForRehydrate<T extends ChatMessage>(messages: T[]): T[] {
  if (!Array.isArray(messages)) return messages;
  let mutated = false;
  const out = messages.map(m => {
    const next = sanitizeMessageForRehydrate(m);
    if (next !== m) mutated = true;
    return next;
  });
  return mutated ? out : messages;
}

/**
 * Sanitize an entire messages array for persistence. Pure helper —
 * exported for the per-thread persist path AND for the legacy global
 * `CHAT_HISTORY` writer that sits outside this module.
 */
export function sanitizeMessagesForPersist<T extends ChatMessage>(messages: T[]): T[] {
  if (!Array.isArray(messages)) return messages;
  let mutated = false;
  const out = messages.map(m => {
    const next = sanitizeMessageForPersist(m);
    if (next !== m) mutated = true;
    return next;
  });
  return mutated ? out : messages;
}

/**
 * Dev-only invariant. Throws when a message about to be persisted
 * still carries a real `File` / `Blob` instance under `attachments` —
 * which would mean a callsite is bypassing the sanitizer and
 * re-introducing the thread-switch attachment-loss regression.
 *
 * Production builds (`process.env.NODE_ENV !== 'development'`) skip
 * the check entirely so this never affects shipped behaviour.
 */
export function assertNoLiveFilesInDev<T extends ChatMessage>(
  messages: T[],
  context: { source: string; threadId: string },
): void {
  // Only fire in dev. Use a safe access pattern that works in both
  // bundler-replaced (`process.env.NODE_ENV` is inlined) and node
  // (`process.env` is a real object) environments.
  let isDev = false;
  try {
    isDev = typeof process !== 'undefined'
      && !!process?.env
      && process.env.NODE_ENV === 'development';
  } catch {
    isDev = false;
  }
  if (!isDev) return;
  if (!Array.isArray(messages)) return;
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i] as ChatMessage & { attachments?: unknown };
    const att = m?.attachments;
    if (!Array.isArray(att) || att.length === 0) continue;
    for (let j = 0; j < att.length; j += 1) {
      if (isRealFileLike(att[j])) {
        // Throw — we want this to surface immediately in dev so the
        // offending callsite gets a stack trace, not a silent
        // round-trip-after-stringify regression in production.
        throw new Error(
          `[thread-iso:persist] Live File instance reached persist boundary. `
          + `Sanitize at the callsite via sanitizeMessagesForPersist(). `
          + `source=${context.source} threadId=${context.threadId} `
          + `messageIndex=${i} attachmentIndex=${j}`
        );
      }
    }
  }
}

/** BroadcastChannel name used to fan multi-tab thread updates. */
export const THREAD_SYNC_CHANNEL = 'prest0-thread-sync';

/** IndexedDB database + store names used as a quota-overflow backstop. */
const IDB_DB_NAME = 'prest0_thread_storage';
const IDB_STORE = 'thread_messages';
const IDB_VERSION = 1;

/** Source of a write — for telemetry triage when an event fires. */
export type ThreadPersistSource =
  | 'mirror-effect'
  | 'snapshot-active'
  | 'switch-thread'
  | 'new-thread'
  | 'remove-thread'
  | 'bootstrap'
  // Greet-on-focus (2026-07-13): the convergent effect that seeds the
  // personalized greeting into an empty thread the user just LANDED in
  // (reuse focus / switch / boot / account sign-in) — mint-time paths keep
  // their own 'new-thread' source.
  | 'greet-on-focus'
  | 'multi-tab-sync'
  // PR #112 (Miles 2026-05-10): every call that flows through
  // `refs.writeThreadMessagesRef.current` (set up in GlobalChatSystem.tsx)
  // tags itself with this source so the routed-write path is
  // distinguishable in `[thread-iso:persist]` telemetry from the
  // useChatThreads.ts mirror/snapshot/bootstrap paths.
  | 'write-ref-routed'
  // 2026-08-07 (thread-msg-write-guard cleanup): the interrupted-message
  // reconcile poll in useChatThreads previously wrote its merged list to the
  // per-thread key DIRECTLY — the last unrouted production write. Routing it
  // here also gives that path the empty-over-non-empty block for free.
  | 'replay-reconcile'
  | 'tests';

/**
 * Compute the per-thread localStorage key for a given (instance,
 * threadId). Mirrors `threadMsgKey` in useChatThreads.ts; duplicated
 * here so this module has no upstream dependency on that hook.
 */
export function threadMsgKey(instanceId: string, threadId: string, uid?: string | null): string {
  return `${getThreadStorageKeys(instanceId, uid).THREAD_MSG_PREFIX}${threadId}`;
}

/**
 * True when defensive guards are disabled by the global feature
 * flag. Defaults to false (guards enabled). Ops can flip to `true`
 * via `window.__PRESTON_THREAD_HISTORY_GUARDS_DISABLED__ = true` in
 * the page console or via a config script.
 */
function guardsDisabled(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window as unknown as { __PRESTON_THREAD_HISTORY_GUARDS_DISABLED__?: boolean })
    .__PRESTON_THREAD_HISTORY_GUARDS_DISABLED__;
}

/**
 * The thread cache's IndexedDB, one connection per page (2026-09-28): it used
 * to open a new connection for every read and write and never close one, so
 * every open tab held the database and a sign-out's delete waited on all of
 * them. idbConnection.ts closes it when another tab or a delete asks, and
 * answers null when an open takes longer than OPEN_TIMEOUT_MS.
 */
const threadCacheDb = createIdbConnection({
  name: IDB_DB_NAME,
  version: IDB_VERSION,
  upgrade: (db) => {
    if (!db.objectStoreNames.contains(IDB_STORE)) {
      db.createObjectStore(IDB_STORE);
    }
  },
});

/** The thread cache's IndexedDB connection, or null on environments without IDB. */
function openIdb(): Promise<IDBDatabase | null> {
  return threadCacheDb.get();
}

/** Read a value from IDB by key. Returns undefined when missing or unavailable. */
async function readFromIdb<T>(key: string): Promise<T | undefined> {
  const db = await openIdb();
  if (!db) return undefined;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        const v = req.result as { value: T; updatedAt: number } | undefined;
        resolve(v?.value);
      };
      req.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

/** Read the IDB envelope (value + updatedAt) for rehydrate-conflict detection. */
async function readIdbEnvelope<T>(
  key: string,
): Promise<{ value: T; updatedAt: number } | undefined> {
  const db = await openIdb();
  if (!db) return undefined;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        resolve(req.result as { value: T; updatedAt: number } | undefined);
      };
      req.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

/**
 * M5 (INCIDENT-20260818): generic JSON write with the same IDB fallback the
 * per-thread transcripts already have. The THREAD LIST previously rode a bare
 * setToLocalStorage whose QuotaExceededError was swallowed with an empty
 * catch — with a full localStorage, a freshly created thread's RECORD never
 * landed on disk while its transcript did, so a reload restored the
 * pre-creation list and the thread was gone. localStorage stays primary;
 * the IDB mirror is written ALWAYS (cheap, newest-wins on recovery).
 */
export async function writeJsonWithIdbFallback<T>(
  key: string,
  value: T,
): Promise<'localStorage' | 'indexedDB' | 'failed'> {
  let localOk = false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, JSON.stringify(value));
      localOk = true;
    }
  } catch {
    /* quota or unavailable — the IDB mirror below is the fallback */
  }
  const idbOk = await writeToIdb(key, value);
  if (localOk) return 'localStorage';
  return idbOk ? 'indexedDB' : 'failed';
}

/** Companion read for the IDB mirror (recovery merge on hydrate). */
export async function readJsonFromIdb<T>(key: string): Promise<T | undefined> {
  return readFromIdb<T>(key);
}

/** Best-effort write to IDB with an updatedAt timestamp envelope. */
async function writeToIdb<T>(key: string, value: T): Promise<boolean> {
  const db = await openIdb();
  if (!db) return false;
  return new Promise(resolve => {
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      store.put({ value, updatedAt: Date.now() }, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Lazy-singleton BroadcastChannel for multi-tab same-thread sync.
 * Returns null in environments without BroadcastChannel.
 */
let _bc: BroadcastChannel | null = null;
let _bcInitTried = false;
/**
 * What a thread-persist signal says about the write it announces (2026-09-23).
 * `tier` is where the bytes actually landed — the SafePersistResult vocabulary.
 * Absent on a signal from an older bundle, which only ever announced writes it
 * believed reached localStorage.
 */
export interface ThreadSyncSignal {
  tier?: SafePersistResult['tier'];
}

interface LocalThreadSyncEntry {
  handler: (signal: ThreadSyncSignal) => void;
  /** Identity of the subscribing view — writes carrying the same token skip it. */
  token?: string;
}
const localThreadSyncListeners = new Map<string, Set<LocalThreadSyncEntry>>();
const localUiSyncListeners = new Map<string, Set<(payload: ChatUiStateSyncPayload) => void>>();

function threadSyncLocalKey(instanceId: string, threadId: string): string {
  return `${instanceId}::${threadId}`;
}

function uiSyncLocalKey(instanceId: string, pagePath: string): string {
  // P4 (surface-invariant UI mode): chat mode is global, so the sync channel
  // must NOT partition by page — a dock toggle in a tab on '/' must reach a
  // tab on '/desktop'. pagePath is kept in the signature/payload for logging.
  void pagePath;
  return instanceId;
}

function notifyLocalThreadSync(
  instanceId: string,
  threadId: string,
  writerToken: string | undefined,
  signal: ThreadSyncSignal,
): void {
  const listeners = localThreadSyncListeners.get(threadSyncLocalKey(instanceId, threadId));
  if (!listeners || listeners.size === 0) return;
  listeners.forEach((entry) => {
    // ECHO-LOOP FIX (OOM incident, Miles 2026-07-12): a write must never
    // notify the view that made it. The same-tab fanout (PR #467) notified
    // ALL local listeners including the writer's own subscription — and the
    // writer's handler dispatches SET_MESSAGES, whose fresh identity re-fires
    // the writer's mirror-persist effect: persist → notify(self) → dispatch →
    // persist… an unbounded same-tab loop carrying full-transcript
    // JSON.stringify + a Firestore upsert + a reconcile chain PER CYCLE.
    if (writerToken && entry.token && entry.token === writerToken) return;
    try { entry.handler(signal); } catch { /* ignore listener failure */ }
  });
}

function notifyLocalUiSync(instanceId: string, pagePath: string, payload: ChatUiStateSyncPayload): void {
  const listeners = localUiSyncListeners.get(uiSyncLocalKey(instanceId, pagePath));
  if (!listeners || listeners.size === 0) return;
  listeners.forEach((listener) => {
    try { listener(payload); } catch { /* ignore listener failure */ }
  });
}

function getBroadcastChannel(): BroadcastChannel | null {
  if (_bcInitTried) return _bc;
  _bcInitTried = true;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    _bc = new BroadcastChannel(THREAD_SYNC_CHANNEL);
  } catch {
    _bc = null;
  }
  return _bc;
}

/**
 * G3 guest-era stamp (review finding 1, 2026-07-15): mark the UNSCOPED store
 * as "written by a guest era of a stamp-aware build". Called from the persist
 * choke point below on every write that actually LANDS without a uid — every
 * guest transcript write (mirror-effect, bootstrap, greet-on-focus, routed
 * writes) flows through `safelyPersistThreadMessages`, so this is the single
 * cheapest reliable place to stamp. Idempotent (first write wins).
 *
 * Deliberately NOT stamped on:
 *   • uid-scoped writes — a member era says nothing about the unscoped store;
 *   • `skippedUnchanged` echoes — re-persisting byte-identical content proves
 *     the bytes were ALREADY in the store, so a pane merely REHYDRATED from
 *     pre-G3 residue can never stamp that residue as guest-owned;
 *   • blocked/failed writes — nothing landed.
 * `adoptGuestThreadsToServer` requires this stamp before any server push;
 * `purgeThreadStorageForSessionReset` clears it so each guest era re-stamps.
 */
function stampGuestEraForUnscopedWrite(instanceId: string, uid?: string | null): void {
  if (typeof uid === 'string' && uid.trim().length > 0) return; // scoped write
  try {
    const key = getGuestEraMarkerKey(instanceId);
    if (localStorage.getItem(key) !== null) return; // already stamped
    localStorage.setItem(key, new Date().toISOString());
  } catch { /* best-effort — an unstamped era degrades to local-only adoption */ }
}

/**
 * Result type for `safelyPersistThreadMessages`. Useful for tests
 * and for the rare caller that wants to know which storage tier the
 * write landed in.
 */
export interface SafePersistResult {
  /** True iff anything was written. False on persist-blocked. */
  written: boolean;
  /** Where the write landed (or which tier was attempted). */
  tier: 'localStorage' | 'indexedDB' | 'blocked' | 'guards-disabled';
  /** True when the rule "empty over non-empty" blocked the write. */
  blockedEmptyOverNonEmpty: boolean;
  /** True when the write was a >50% shrink vs. existing content. */
  suspiciousShrink: boolean;
  /** True when localStorage threw QuotaExceeded and IDB took over. */
  quotaExceededFallback: boolean;
  /** True when the write+broadcast were elided because the stored content was byte-identical. */
  skippedUnchanged?: boolean;
}

/**
 * Decide what to do with a persist call WITHOUT touching storage.
 * Pure for unit tests. Returns the same result you'd get if we ran
 * the full pipeline against the supplied existing/next values.
 */
export function decidePersistAction<T extends ChatMessage>(args: {
  existing: T[];
  next: T[];
  guardsDisabled: boolean;
}): {
  action: 'block' | 'write';
  blockedEmptyOverNonEmpty: boolean;
  suspiciousShrink: boolean;
} {
  if (args.guardsDisabled) {
    return { action: 'write', blockedEmptyOverNonEmpty: false, suspiciousShrink: false };
  }
  const existingLen = Array.isArray(args.existing) ? args.existing.length : 0;
  const nextLen = Array.isArray(args.next) ? args.next.length : 0;

  // Rule 1: never wipe a non-empty thread with an empty snapshot.
  if (existingLen > 0 && nextLen === 0) {
    return { action: 'block', blockedEmptyOverNonEmpty: true, suspiciousShrink: false };
  }

  // Rule 2: log-only suspicious shrink (>50% drop, but allow because
  // the user may have genuinely deleted content).
  if (existingLen > 0 && nextLen > 0 && nextLen < existingLen / 2) {
    return { action: 'write', blockedEmptyOverNonEmpty: false, suspiciousShrink: true };
  }

  return { action: 'write', blockedEmptyOverNonEmpty: false, suspiciousShrink: false };
}

/**
 * The canonical per-thread persist function.
 *
 * Replaces every direct `setToLocalStorage(threadMsgKey(...), msgs)`
 * call in useChatThreads.ts. Implements:
 *   • the empty-over-non-empty guard
 *   • the suspicious-shrink telemetry
 *   • the localStorage → IndexedDB fallback on QuotaExceededError
 *   • the BroadcastChannel multi-tab signal on every successful write
 */
export function safelyPersistThreadMessages<T extends ChatMessage>(args: {
  instanceId: string;
  threadId: string;
  messages: T[];
  source: ThreadPersistSource;
  uid?: string | null;
  /** Identity of the writing view — its own thread-sync subscription is skipped. */
  writerToken?: string;
}): SafePersistResult {
  // The chat's health record (2026-09-28): how long a whole-thread save takes
  // (it runs on the main thread, while someone types).
  const savedAt = Date.now();
  try {
    return persistThreadMessagesNow(args);
  } finally {
    noteTiming('persist', Date.now() - savedAt);
  }
}

function persistThreadMessagesNow<T extends ChatMessage>(args: {
  instanceId: string;
  threadId: string;
  messages: T[];
  source: ThreadPersistSource;
  uid?: string | null;
  writerToken?: string;
}): SafePersistResult {
  const { instanceId, threadId, source, uid, writerToken } = args;
  const key = threadMsgKey(instanceId, threadId, uid);

  // A save held back for this key (threadCacheTier, 2026-09-28) lands first,
  // so writes keep the order they were asked for in.
  flushDeferredThreadSave(key);

  // Dev-only invariant: catch any callsite that hands us a live File[].
  // Fires BEFORE sanitization so the stack points at the offender.
  // No-op in production builds.
  assertNoLiveFilesInDev(args.messages, { source, threadId });

  // A SIGNED-IN THREAD'S COPY IS ITS NEWEST WINDOW (localCopyWindow.ts,
  // 2026-10-24): the server holds every message, and saving a whole shared
  // room on every change held the page for a third of a second. A guest's copy
  // stays whole (until sign-in adopts it, it can be the only copy). The guards
  // below judge the whole list the caller handed in.
  const signedIn = typeof uid === 'string' && uid.trim() !== '';
  // Strip non-roundtrippable runtime values (today: File[]) before any
  // storage write. This is the canonical persistence boundary for
  // per-thread snapshots; doing it here means EVERY write site —
  // mirror-effect, snapshot-active, switch-thread, write-ref-routed —
  // is automatically protected without per-callsite plumbing.
  const messages: T[] = sanitizeMessagesForPersist(signedIn ? newestLocalWindow(args.messages) : args.messages);

  if (guardsDisabled()) {
    setToLocalStorage(key, messages);
    stampGuestEraForUnscopedWrite(instanceId, uid);
    broadcastWrite(instanceId, threadId, messages.length, source, writerToken, 'guards-disabled');
    return {
      written: true,
      tier: 'guards-disabled',
      blockedEmptyOverNonEmpty: false,
      suspiciousShrink: false,
      quotaExceededFallback: false,
    };
  }

  // ECHO-LOOP FIX / no-op write elision (OOM incident, Miles 2026-07-12):
  // an unchanged snapshot must not write NOR broadcast. Re-persisting
  // byte-identical content re-fired the sync fanout, whose consumers dispatch
  // SET_MESSAGES — and the resulting fresh array identity re-fired the
  // mirror-persist effect: the storage layer itself was the loop's engine
  // (same-tab synchronously; cross-tab as BroadcastChannel ping-pong). The
  // stored string is the exact JSON.stringify this function would write, so
  // string equality IS content equality; any mismatch (even key order) safely
  // degrades to a redundant-but-convergent write.
  //
  // THE HELD COPY IS THE BASELINE WHEN THERE IS ONE (2026-09-23): after the full
  // bin refused this key, localStorage holds a FROZEN older list, so comparing
  // against it made every later pass "changed" — each one re-ran the refused
  // write, an 8x-payload reclaim that evicted every other thread's cache, and the
  // IndexedDB lane, for content this tab had already tried to store. Content
  // identical to the held copy was already handed to the IndexedDB lane.
  let serialized: string | null = null;
  try {
    serialized = JSON.stringify(messages);
    const heldNow = readHeldCopy(key);
    const storedNow = heldNow ?? localStorage.getItem(key);
    if (serialized !== null && storedNow === serialized) {
      return {
        written: true, // content IS durably persisted — just not by this call
        tier: heldNow !== null ? 'indexedDB' : 'localStorage',
        blockedEmptyOverNonEmpty: false,
        suspiciousShrink: false,
        quotaExceededFallback: false,
        skippedUnchanged: true,
      };
    }
  } catch { serialized = null; /* fall through to the normal write path */ }

  const existing = getFromLocalStorage<T[]>(key, []);
  const decision = decidePersistAction({
    existing,
    next: args.messages,
    guardsDisabled: false,
  });

  if (decision.action === 'block') {
    // Telemetry: persist-blocked. Surfaces in production logs so we
    // can spot any new callsite that tries to wipe on resume / dormancy.
    try {
      // eslint-disable-next-line no-console
      console.warn('[thread-iso:persist] thread.history.persist-blocked', {
        instanceId,
        threadId,
        existingCount: existing.length,
        newCount: messages.length,
        source,
      });
    } catch { /* ignore */ }
    return {
      written: false,
      tier: 'blocked',
      blockedEmptyOverNonEmpty: true,
      suspiciousShrink: false,
      quotaExceededFallback: false,
    };
  }

  if (decision.suspiciousShrink) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[thread-iso:persist] thread.history.suspicious-shrink', {
        instanceId,
        threadId,
        existingCount: existing.length,
        newCount: messages.length,
        source,
      });
    } catch { /* ignore */ }
  }

  // Try localStorage first.
  try {
    localStorage.setItem(key, serialized ?? JSON.stringify(messages));
    releaseHeldCopy(key);
    stampGuestEraForUnscopedWrite(instanceId, uid);
    broadcastWrite(instanceId, threadId, messages.length, source, writerToken, 'localStorage');
    // Keep the bin from filling during a long session (at most once a minute;
    // never evicts the key just saved or the open thread).
    try { sweepStorageDuringSession([key]); } catch { /* a sweep never fails a save */ }
    return {
      written: true,
      tier: 'localStorage',
      blockedEmptyOverNonEmpty: false,
      suspiciousShrink: decision.suspiciousShrink,
      quotaExceededFallback: false,
    };
  } catch (storageError) {
    // QuotaExceededError detection that works without DOMException
    // being globally defined (e.g. node test env). DOMException is
    // present in real browsers; we guard via typeof to keep the
    // helper portable.
    const errAny = storageError as { name?: string; code?: number };
    const hasDomEx = typeof DOMException !== 'undefined';
    const isQuota = (
      (hasDomEx && storageError instanceof DOMException)
      || errAny?.name === 'QuotaExceededError'
    ) && (errAny?.name === 'QuotaExceededError' || errAny?.code === 22);
    if (!isQuota) {
      // Non-quota error: log and give up (matches the legacy
      // setToLocalStorage swallow behaviour for non-quota errors).
      try {
        // eslint-disable-next-line no-console
        console.warn('[thread-iso:persist] localStorage write failed (non-quota)', {
          instanceId,
          threadId,
          error: String(storageError),
          source,
        });
      } catch { /* ignore */ }
      return {
        written: false,
        tier: 'localStorage',
        blockedEmptyOverNonEmpty: false,
        suspiciousShrink: decision.suspiciousShrink,
        quotaExceededFallback: false,
      };
    }

    // QuotaExceeded: telemetry + IDB fallback.
    try {
      // eslint-disable-next-line no-console
      console.warn('[thread-iso:persist] thread.history.quota-exceeded', {
        instanceId,
        threadId,
        newCount: messages.length,
        source,
      });
    } catch { /* ignore */ }

    // QUOTA SELF-HEAL (2026-08-22, b815 incident): before splitting the
    // truth across stores, evict cold thread caches and retry localStorage —
    // an IDB-only copy leaves the PRIMARY store holding a stale snapshot
    // (the frozen-rail / stuck-"Queued" class) and leaves the bin full for
    // the Firestore SDK's own writes (the page-crash class). IDB stays the
    // fallback of last resort when even eviction can't make room.
    try {
      const payload = serialized ?? JSON.stringify(messages);
      const freed = reclaimThreadCacheBytes(
        Math.max((key.length + payload.length) * 2 * 4, 262_144),
        [key],
      );
      if (freed > 0) {
        localStorage.setItem(key, payload);
        releaseHeldCopy(key);
        stampGuestEraForUnscopedWrite(instanceId, uid);
        broadcastWrite(instanceId, threadId, messages.length, source, writerToken, 'localStorage');
        return {
          written: true,
          tier: 'localStorage',
          blockedEmptyOverNonEmpty: false,
          suspiciousShrink: decision.suspiciousShrink,
          quotaExceededFallback: false,
        };
      }
    } catch { /* eviction/retry failed — fall through to the IDB lane */ }

    // localStorage is now FROZEN behind this tab's list for this key. Hold the
    // list this tab tried to store — synchronously, before anything else in
    // this tab can read the key — so the reader behind addMessage's base, the
    // queued-turn lane and the persist guards see this tab's copy, never the
    // frozen one (threadCacheTier; 2026-09-23, the newest-message flicker).
    try { holdRefusedWrite(key, serialized ?? JSON.stringify(messages)); } catch { /* best-effort */ }

    // Fire the IDB write but don't block the synchronous return —
    // callers don't await this and we don't want to introduce
    // async behaviour where the existing API was sync. The return
    // value reflects the FALLBACK *attempt*; the actual IDB result
    // is reported via a second telemetry event.
    void (async () => {
      const ok = await writeToIdb(key, { messages, updatedAt: Date.now() });
      try {
        // eslint-disable-next-line no-console
        console.warn('[thread-iso:persist] thread.history.indexeddb-fallback', {
          instanceId,
          threadId,
          newCount: messages.length,
          source,
          ok,
        });
      } catch { /* ignore */ }
      // NO BROADCAST from this lane (2026-09-23). Every consumer of the signal
      // re-reads localStorage — and localStorage does not hold what was just
      // written: it holds the FROZEN older copy. Announcing this write told every
      // other view to adopt that frozen copy, which removed the newest rows from
      // its pane until its server listener put them back, whose persist then did
      // the same to this view: Miles's newest message in a group thread
      // flickering in and out (2026-09-22 recording). Other views learn this
      // content from the server listener, like another device's. Staying silent
      // (rather than announcing tier 'indexedDB') also protects a tab still
      // running an older bundle, which cannot read the tier.
      if (ok) {
        stampGuestEraForUnscopedWrite(instanceId, uid);
      }
    })();

    return {
      written: true, // IDB write is in flight; treat as eventual success
      tier: 'indexedDB',
      blockedEmptyOverNonEmpty: false,
      suspiciousShrink: decision.suspiciousShrink,
      quotaExceededFallback: true,
    };
  }
}

/**
 * Notify other tabs in the same browser that a per-thread write
 * happened. No-op on environments without BroadcastChannel.
 */
function broadcastWrite(
  instanceId: string,
  threadId: string,
  count: number,
  source: ThreadPersistSource,
  writerToken: string | undefined,
  tier: SafePersistResult['tier'],
): void {
  const bc = getBroadcastChannel();
  if (!bc) return;
  try {
    bc.postMessage({
      type: 'thread-persist',
      instanceId,
      threadId,
      count,
      source,
      tier,
      at: Date.now(),
    });
  } catch { /* ignore */ }
  notifyLocalThreadSync(instanceId, threadId, writerToken, { tier });
}

/**
 * Subscribe to multi-tab thread-persist signals. Returns an
 * unsubscribe function. Calls `handler` whenever another tab in the
 * same browser persists a thread for the same `(instanceId,
 * threadId)`. Caller is responsible for re-reading storage and
 * dispatching a SET_MESSAGES.
 */
export function subscribeToThreadSync(
  args: {
    instanceId: string;
    threadId: string;
    /**
     * Identity of the subscribing view. Persists made with the same
     * `writerToken` skip this subscription (a view never needs to be told
     * about its own writes — its state already reflects them; see the
     * echo-loop note in notifyLocalThreadSync).
     */
    token?: string;
  },
  /**
   * Called with what the signal says about the write (see ThreadSyncSignal). A
   * handler that re-reads localStorage should do so only for a write that
   * landed there — the tier says which.
   */
  handler: (signal: ThreadSyncSignal) => void,
): () => void {
  const bc = getBroadcastChannel();
  const localKey = threadSyncLocalKey(args.instanceId, args.threadId);
  const entry: LocalThreadSyncEntry = { handler, token: args.token };
  const localSet = localThreadSyncListeners.get(localKey) || new Set<LocalThreadSyncEntry>();
  localSet.add(entry);
  localThreadSyncListeners.set(localKey, localSet);

  if (!bc) {
    return () => {
      const listeners = localThreadSyncListeners.get(localKey);
      if (!listeners) return;
      listeners.delete(entry);
      if (listeners.size === 0) localThreadSyncListeners.delete(localKey);
    };
  }
  const onMsg = (e: MessageEvent) => {
    const data = e.data as undefined | {
      type?: string; instanceId?: string; threadId?: string; tier?: unknown;
    };
    if (!data || data.type !== 'thread-persist') return;
    if (data.instanceId !== args.instanceId) return;
    if (data.threadId !== args.threadId) return;
    try {
      // eslint-disable-next-line no-console
      console.warn('[thread-iso:persist] thread.history.multi-tab-sync', {
        instanceId: args.instanceId,
        threadId: args.threadId,
      });
    } catch { /* ignore */ }
    const tier = typeof data.tier === 'string' ? (data.tier as SafePersistResult['tier']) : undefined;
    handler(tier ? { tier } : {});
  };
  bc.addEventListener('message', onMsg);
  return () => {
    try { bc.removeEventListener('message', onMsg); } catch { /* ignore */ }
    const listeners = localThreadSyncListeners.get(localKey);
    if (!listeners) return;
    listeners.delete(entry);
    if (listeners.size === 0) localThreadSyncListeners.delete(localKey);
  };
}

export interface ChatUiStateSyncPayload {
  isChatOpen?: boolean;
  isChatDockedRight?: boolean;
  isChatFullScreen?: boolean;
}

/**
 * Publish an open/dock/fullscreen change to the OTHER chat surfaces of THIS
 * tab (widget ↔ landing-page mirror) via the local listener map.
 *
 * Per-tab view independence (2026-08-09): this used to ALSO post the payload
 * on the BroadcastChannel, so toggling full-screen in one tab yanked every
 * sibling tab into the same view. View state is now strictly per-tab — the
 * persisted mode flags (types.ts GLOBAL_* keys) still initialize a NEW tab
 * from the last-saved view, but a live tab never adopts another tab's view.
 * Thread CONTENT sync (type 'thread-persist') is untouched.
 */
export function publishChatUiStateSync(args: {
  instanceId: string;
  pagePath: string;
  payload: ChatUiStateSyncPayload;
  source: string;
}): void {
  notifyLocalUiSync(args.instanceId, args.pagePath, args.payload);
}

/**
 * Subscribe to SAME-TAB chat view-state changes (see publishChatUiStateSync —
 * cross-tab delivery was removed 2026-08-09; view state is per-tab).
 */
export function subscribeToChatUiStateSync(
  args: { instanceId: string; pagePath: string },
  handler: (payload: ChatUiStateSyncPayload) => void,
): () => void {
  const localKey = uiSyncLocalKey(args.instanceId, args.pagePath);
  const localSet = localUiSyncListeners.get(localKey) || new Set<(payload: ChatUiStateSyncPayload) => void>();
  localSet.add(handler);
  localUiSyncListeners.set(localKey, localSet);
  return () => {
    const listeners = localUiSyncListeners.get(localKey);
    if (!listeners) return;
    listeners.delete(handler);
    if (listeners.size === 0) localUiSyncListeners.delete(localKey);
  };
}

/**
 * Rehydrate a thread's messages on bootstrap. Reads BOTH localStorage
 * and IndexedDB; if both are present and disagree, picks the newer
 * (by updatedAt for IDB / write order for LS) and logs
 * `thread.history.rehydrate-conflict`.
 *
 * Returns the chosen messages array. `undefined` on neither side has
 * a value.
 */
export async function rehydrateThreadMessages<T extends ChatMessage>(args: {
  instanceId: string;
  threadId: string;
  uid?: string | null;
}): Promise<T[] | undefined> {
  const key = threadMsgKey(args.instanceId, args.threadId, args.uid);
  // A save held back for this key lands before it is read.
  flushDeferredThreadSave(key);
  const ls = (() => {
    try {
      // This tab's held copy stands in for a frozen localStorage copy.
      const raw = readHeldCopy(key) ?? localStorage.getItem(key);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as T[]) : undefined;
    } catch { return undefined; }
  })();
  const idbEnv = await readIdbEnvelope<{ messages: T[]; updatedAt: number }>(key);
  if (!ls && !idbEnv) return undefined;
  if (ls && !idbEnv) return ls;
  if (!ls && idbEnv) return idbEnv.value.messages;
  // Both present. The database copy is the save localStorage refused, so it
  // wins only when it reaches a NEWER message (2026-10-24). It used to win by
  // being longer, but a signed-in local copy is now the thread's newest window
  // (localCopyWindow.ts): a longer database copy is an older save that kept
  // more history, and painting it would hide the newest messages.
  const lsLen = ls ? ls.length : 0;
  const idbMessages = idbEnv?.value?.messages ?? [];
  const idbLen = idbMessages.length;
  if (lsLen !== idbLen) {
    try {
      // eslint-disable-next-line no-console
      console.warn('[thread-iso:persist] thread.history.rehydrate-conflict', {
        instanceId: args.instanceId,
        threadId: args.threadId,
        lsLen,
        idbLen,
        idbUpdatedAt: idbEnv?.updatedAt,
      });
    } catch { /* ignore */ }
  }
  return newestMessageTime(idbMessages) > newestMessageTime(ls as T[]) ? idbMessages : (ls as T[]);
}

/**
 * Read IDB for a thread synchronously is not possible — kept here as
 * a tests-only helper used by `readFromIdb` in production code paths
 * where we already accept async behaviour.
 */
export async function readThreadMessagesFromIdbForTests<T extends ChatMessage>(
  instanceId: string,
  threadId: string,
  uid?: string | null,
): Promise<T[] | undefined> {
  const key = threadMsgKey(instanceId, threadId, uid);
  const env = await readFromIdb<{ messages: T[]; updatedAt: number }>(key);
  return env?.messages;
}

// ────────────────────────────────────────────────────────────────────────────
// Clear-chat helpers (Miles 2026-05-12, Slack thread C0AM8U804KZ/1778481193.723179)
//
// `safelyPersistThreadMessages` deliberately blocks empty-over-non-empty writes
// so a stale tab-resume can't wipe a non-empty transcript. The clear-chat
// flow needs the OPPOSITE behaviour: explicit, full purge across every
// per-thread key for the instance — including any keys for archived threads
// or threads that no longer appear in the UI's active list (orphans from a
// previous bug). Rather than threading a "force" flag through the
// safe-persist API (which would create a footgun), we expose a dedicated
// `purgeAllThreadStorage` helper that bypasses the guards entirely.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Synchronously remove every per-thread message key for an instance from
 * localStorage. Best-effort — wraps each removeItem in try/catch so a
 * single corrupt entry never aborts the sweep.
 *
 * Returns the list of keys that were removed (mostly for tests + telemetry).
 */
export function purgeAllThreadStorage(instanceId: string, uid?: string | null): string[] {
  if (!instanceId || typeof localStorage === 'undefined') return [];
  const prefix = getThreadStorageKeys(instanceId, uid).THREAD_MSG_PREFIX;
  const removed: string[] = [];
  // Snapshot keys first because removeItem mutates the iteration order.
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
  } catch {
    return removed;
  }
  for (const k of keys) {
    try {
      localStorage.removeItem(k);
      removed.push(k);
    } catch { /* ignore — best-effort sweep */ }
  }
  // A held copy (threadCacheTier) is this tab's copy of a key the full bin
  // refused — it may exist with NO localStorage key at all, so it is released by
  // prefix, not by the keys just enumerated. A purge that left it would hand the
  // purged transcript back to the next read of the key.
  releaseHeldCopiesWhere((k) => k.startsWith(prefix));
  // Fire-and-forget IDB purge so the quota-overflow backstop is wiped too.
  // The IDB store is keyed by the SAME `threadMsgKey(instanceId, threadId)`
  // string we just removed from localStorage, so iterating the just-removed
  // keys is the precise set we need to clear.
  void (async () => {
    const db = await openIdb();
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      for (const k of keys) {
        try { store.delete(k); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  })();
  try {
    // eslint-disable-next-line no-console
    console.warn('[thread-iso:persist] thread.history.purge-all', {
      instanceId,
      removedCount: removed.length,
    });
  } catch { /* ignore */ }
  return removed;
}

/**
 * Purge thread-switcher storage for a session reset boundary.
 *
 * Removes:
 * - Legacy (unscoped) metadata keys + per-thread message keys for `instanceId`
 * - Departing user's uid-scoped metadata keys + per-thread message keys
 *
 * Does NOT touch other uid namespaces, preserving previously-saved history for
 * unrelated users in the same browser profile.
 */
export function purgeThreadStorageForSessionReset(
  instanceId: string,
  departingUid?: string | null,
): string[] {
  if (!instanceId || typeof localStorage === 'undefined') return [];

  const removed = new Set<string>();
  const removeKey = (key: string) => {
    try {
      if (localStorage.getItem(key) === null) return;
      localStorage.removeItem(key);
      removed.add(key);
    } catch {
      // ignore
    }
  };

  const legacy = getThreadStorageKeys(instanceId);
  removeKey(legacy.THREAD_LIST);
  removeKey(legacy.ACTIVE_THREAD);
  removeKey(legacy.THREAD_ARCHIVE);
  // G3 guest-era stamp: the unscoped store is being wiped, so the era it
  // described is over. A NEW guest era re-stamps on its first unscoped write
  // (see stampGuestEraForUnscopedWrite), keeping adoption gated correctly.
  removeKey(getGuestEraMarkerKey(instanceId));
  for (const key of purgeAllThreadStorage(instanceId)) {
    removed.add(key);
  }

  const uid = typeof departingUid === 'string' ? departingUid.trim() : '';
  if (uid) {
    const scoped = getThreadStorageKeys(instanceId, uid);
    removeKey(scoped.THREAD_LIST);
    removeKey(scoped.ACTIVE_THREAD);
    removeKey(scoped.THREAD_ARCHIVE);
    for (const key of purgeAllThreadStorage(instanceId, uid)) {
      removed.add(key);
    }
    // G3: the guest-adoption once-marker only dedupes WITHIN a sign-in era
    // (double hydrate / second tab). Clear it at the sign-out boundary so the
    // NEXT sign-in adopts whatever a new guest era creates.
    removeKey(getGuestAdoptionMarkerKey(instanceId, uid));
    // 2026-08-21 sign-out leak: the departing member's durable archive
    // tombstones are theirs, not the next identity's.
    //
    // Through the SHARED builder since 2026-09-21. It had been a second
    // hand-rolled copy of the key, which is the quietest way this purge could
    // ever fail: change the spelling at the writer and this line keeps
    // deleting a key nothing writes, the departing member's tombstones survive
    // the sign-out, and nothing anywhere goes red. One owner, pinned by a
    // whole-tree scan in tests/groupThreads/archivedTombstones.test.ts.
    removeKey(tombstoneStorageKey(instanceId, uid));
  }

  // 2026-08-21 sign-out leak (owner security report): two stores this purge
  // used to miss kept resurrecting the departed member's threads.
  // (1) The legacy-owner marker holds a uid VALUE in a global key — but it is
  // the ONLY proof that the unscoped legacy keys belong to the RETAINED
  // identity. Remove it only when it names the DEPARTING uid (or no uid was
  // given): deleting a bystander's marker made the next hydrate's
  // cross-account guard purge that user's real legacy threads
  // ("scoped-exists-without-owner-stamp", 2026-08-21 regression).
  const legacyOwnerMarkerKey = `prest0_thread_legacy_owner__${instanceId}`;
  try {
    const markerValue = localStorage.getItem(legacyOwnerMarkerKey);
    if (!uid || !markerValue || markerValue === uid) {
      removeKey(legacyOwnerMarkerKey);
    }
  } catch {
    removeKey(legacyOwnerMarkerKey);
  }
  // (2) removeKey is localStorage-only, but writeJsonWithIdbFallback mirrors
  // the THREAD_LIST (and friends) into IndexedDB unconditionally — the IDB
  // copy survived every sign-out and the recovery effect merged the member's
  // list straight back into the guest's state. Delete the metadata keys from
  // the IDB store too (both namespaces; per-thread transcripts are already
  // swept by purgeAllThreadStorage above).
  const idbDoomed = [
    legacy.THREAD_LIST, legacy.ACTIVE_THREAD, legacy.THREAD_ARCHIVE,
    ...(uid
      ? (() => {
          const scoped = getThreadStorageKeys(instanceId, uid);
          return [scoped.THREAD_LIST, scoped.ACTIVE_THREAD, scoped.THREAD_ARCHIVE];
        })()
      : []),
  ];
  void (async () => {
    const db = await openIdb();
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      for (const k of idbDoomed) {
        try { store.delete(k); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  })();

  return Array.from(removed);
}

function parseUidFromThreadCacheKey(key: string): string | null {
  const marker = '__u_';
  const idx = key.indexOf(marker);
  if (idx < 0) return null;
  const start = idx + marker.length;
  const end = key.indexOf('__', start);
  if (end < 0) {
    const uid = key.slice(start);
    return uid || null;
  }
  const uid = key.slice(start, end);
  return uid || null;
}

function shouldKeepThreadCacheKey(key: string, keepUid: string | null): boolean {
  if (!keepUid) return false;
  const keyUid = parseUidFromThreadCacheKey(key);
  return keyUid === keepUid;
}

/**
 * Purge thread cache keys across localStorage and IndexedDB.
 *
 * When `keepUid` is provided, keeps only keys for that uid.
 */
export function purgeThreadCache(opts?: { keepUid?: string | null }): string[] {
  const keepUidRaw = opts?.keepUid;
  const keepUid = typeof keepUidRaw === 'string' && keepUidRaw.trim().length > 0
    ? keepUidRaw.trim()
    : null;
  if (typeof localStorage === 'undefined') return [];

  const rootPrefixes = [
    'prest0_threads__',
    'prest0_active_thread__',
    'prest0_thread_msgs__',
    'prest0_thread_archive__',
  ];
  const removed: string[] = [];
  const keys: string[] = [];

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!rootPrefixes.some((prefix) => key.startsWith(prefix))) continue;
      if (shouldKeepThreadCacheKey(key, keepUid)) continue;
      keys.push(key);
    }
  } catch {
    return removed;
  }

  for (const key of keys) {
    try {
      localStorage.removeItem(key);
      removed.push(key);
    } catch {
      // ignore
    }
  }
  // Held copies follow the same keep rule (see purgeAllThreadStorage).
  releaseHeldCopiesWhere((key) =>
    rootPrefixes.some((prefix) => key.startsWith(prefix)) && !shouldKeepThreadCacheKey(key, keepUid));

  void (async () => {
    const db = await openIdb();
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      let cursorReq: IDBRequest<IDBCursorWithValue | null>;
      try {
        cursorReq = store.openCursor();
      } catch {
        return;
      }
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return;
        try {
          const key = String(cursor.key || '');
          if (
            rootPrefixes.some((prefix) => key.startsWith(prefix))
            && !shouldKeepThreadCacheKey(key, keepUid)
          ) {
            cursor.delete();
          }
        } catch {
          // ignore
        }
        try {
          cursor.continue();
        } catch {
          // ignore
        }
      };
    } catch {
      // ignore
    }
  })();

  return removed;
}
